// ============================================================================
// SmartLaunchContinueView — SMART Build 2L (Launch Continuation Claim)
//
// The authenticated GP's bridge from a successful SMART callback into ReferralGate.
// Mounted role-agnostically in App.jsx so it owns its OWN role/tenant/age blocked
// copy (never the generic AccessDenied). It:
//   1. gates to active same-tenant gp_employee (server re-enforces),
//   2. reads a SAFE read-only continuation summary (no PHI),
//   3. on confirm: createPacketFromSmartLaunch → importSmartLaunchPatientCoverageCandidates,
//   4. routes to the Packet Inbox, where the existing 2H row continues to the Gate.
//
// It NEVER creates referral truth, never bypasses the Gate, and never reads or
// stores a token / patient id / fhirUser / PHI. The launchSessionId is the only
// value it handles.
// ============================================================================

import React, { useCallback, useEffect, useState } from 'react';
import { ShieldCheck, CheckCircle2, Circle, Loader2, AlertCircle, ArrowRight, Lock } from 'lucide-react';
import {
  createPacketFromSmartLaunch,
  importSmartLaunchPatientCoverageCandidates,
  getSmartLaunchContinuation,
} from '../core/ehrApi.js';
import { defaultRouteForRole } from '../core/index.js';
import {
  getContinuationRoleState,
  getContinuationStatusLines,
  getContinuationCtaState,
  mapContinuationError,
  isValidLaunchSessionId,
} from '../core/smartLaunchContinuationGate.js';
import { clearSmartContinueIntent } from '../core/smartContinueIntent.js';

const Shell = ({ children }) => (
  <div className="max-w-xl mx-auto p-6">
    <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">{children}</div>
  </div>
);

const Header = ({ icon: Icon, tone = 'sky', title, subtitle }) => {
  const toneClass = {
    sky: 'bg-sky-100 text-sky-700',
    red: 'bg-red-100 text-red-700',
    slate: 'bg-slate-100 text-slate-600',
  }[tone] || 'bg-sky-100 text-sky-700';
  return (
    <div className="flex items-start gap-3 mb-4">
      <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${toneClass}`}>
        <Icon className="w-6 h-6" />
      </div>
      <div className="min-w-0">
        <h1 className="text-lg font-semibold text-slate-900">{title}</h1>
        {subtitle && <p className="text-sm text-slate-600 mt-0.5">{subtitle}</p>}
      </div>
    </div>
  );
};

// Blocked / terminal state: a message + a single safe way back into the app.
const BlockedState = ({ title, message, onBack, tone = 'slate' }) => (
  <Shell>
    <Header icon={tone === 'red' ? AlertCircle : Lock} tone={tone} title={title} />
    <p className="text-sm text-slate-700">{message}</p>
    <div className="mt-5">
      <button
        type="button"
        onClick={onBack}
        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-800 text-white text-sm font-medium hover:bg-slate-700"
      >
        Go to ReferralGate
      </button>
    </div>
  </Shell>
);

const StatusLine = ({ label, done }) => (
  <li className="flex items-center gap-2 text-sm">
    {done
      ? <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0" />
      : <Circle className="w-4 h-4 text-slate-300 flex-shrink-0" />}
    <span className={done ? 'text-slate-800' : 'text-slate-400'}>{label}</span>
  </li>
);

const SmartLaunchContinueView = ({ launchSessionId, user, onNavigate }) => {
  const role = user?.role;
  const roleState = getContinuationRoleState({ role, isActive: user?.isActive, tenantId: user?.tenantId });
  const goHome = useCallback(() => {
    clearSmartContinueIntent();
    onNavigate(defaultRouteForRole(role));
  }, [onNavigate, role]);
  const goPacketInbox = useCallback(() => {
    clearSmartContinueIntent();
    onNavigate('packet-inbox');
  }, [onNavigate]);

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [projection, setProjection] = useState(null);

  // create+import flow state
  const [phase, setPhase] = useState('idle'); // idle | creating | importing | done
  const [actionError, setActionError] = useState(null);

  const validId = isValidLaunchSessionId(launchSessionId);
  const canLoad = validId && roleState.allowed;

  const load = useCallback(async () => {
    if (!canLoad) return;
    setLoading(true);
    setLoadError(null);
    try {
      const data = await getSmartLaunchContinuation({ launchSessionId });
      setProjection(data);
    } catch (err) {
      setLoadError(mapContinuationError(err));
    } finally {
      setLoading(false);
    }
  }, [canLoad, launchSessionId]);

  useEffect(() => { load(); }, [load]);

  const handleContinue = useCallback(async () => {
    setActionError(null);
    setPhase('creating');
    try {
      // Strict order — the packet container must exist before import. An idempotent
      // ALREADY_LINKED result resolves (ok:true) and is forward motion, not failure.
      await createPacketFromSmartLaunch({ launchSessionId });
      setPhase('importing');
      const imported = await importSmartLaunchPatientCoverageCandidates({ launchSessionId });
      if (imported && imported.ehrImportStatus === 'FAILED') {
        setActionError('EHR import did not complete. Please try again, or launch again from the EHR.');
        setPhase('idle');
        return;
      }
      setPhase('done');
      goPacketInbox();
    } catch (err) {
      setActionError(mapContinuationError(err));
      setPhase('idle');
    }
  }, [launchSessionId, goPacketInbox]);

  // ── Pre-flight blocked states (no fetch / no mutation) ──────────────────────
  if (!validId) {
    return (
      <BlockedState
        tone="red"
        title="SMART Launch Continuation"
        message="Launch session not found. Please launch again from the EHR."
        onBack={goHome}
      />
    );
  }
  if (!roleState.allowed) {
    return (
      <BlockedState
        tone="slate"
        title="SMART Launch Continuation"
        message={roleState.message}
        onBack={goHome}
      />
    );
  }

  // ── Loading the read-only summary ───────────────────────────────────────────
  if (loading) {
    return (
      <Shell>
        <Header icon={ShieldCheck} title="SMART Launch Continuation" subtitle="Checking your EHR launch…" />
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading launch status…
        </div>
      </Shell>
    );
  }

  // ── Load failed → safe mapped copy + retry / relaunch ───────────────────────
  if (loadError) {
    return (
      <Shell>
        <Header icon={AlertCircle} tone="red" title="SMART Launch Continuation" />
        <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700 flex items-start gap-2">
          <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <div>{loadError}</div>
        </div>
        <div className="mt-5 flex items-center gap-2">
          <button
            type="button"
            onClick={load}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-slate-300 text-slate-700 text-sm font-medium hover:bg-slate-50"
          >
            Try again
          </button>
          <button
            type="button"
            onClick={goHome}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-800 text-white text-sm font-medium hover:bg-slate-700"
          >
            Go to ReferralGate
          </button>
        </div>
      </Shell>
    );
  }

  // ── Loaded — render status + CTA ────────────────────────────────────────────
  const cta = getContinuationCtaState(projection);
  const lines = getContinuationStatusLines(projection);
  const sourceLabel = projection?.sourceSystemLabel || 'your EHR';
  const busy = phase === 'creating' || phase === 'importing';

  // Forward state isn't actionable (expired / no patient context).
  if (cta.mode === 'BLOCKED') {
    const blockedMsg = projection?.nextAction === 'RELAUNCH'
      ? 'This EHR launch has expired. Please launch again from the EHR.'
      : 'Patient context was not received for this EHR launch. Please launch again from the EHR.';
    return (
      <BlockedState tone="red" title="SMART Launch Continuation" message={blockedMsg} onBack={goHome} />
    );
  }

  return (
    <Shell>
      <Header
        icon={ShieldCheck}
        title="SMART Launch Continuation"
        subtitle={`Continue the launch from ${sourceLabel} to create a referral packet and review imported EHR candidates.`}
      />

      <ul className="space-y-2 mb-5">
        {lines.map((l) => <StatusLine key={l.label} label={l.label} done={l.done} />)}
      </ul>

      {actionError && (
        <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700 flex items-start gap-2 mb-4">
          <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <div>{actionError}</div>
        </div>
      )}

      {busy && (
        <div className="flex items-center gap-2 text-sm text-sky-700 mb-4">
          <Loader2 className="w-4 h-4 animate-spin" />
          {phase === 'creating' ? 'Creating referral packet…' : 'Importing EHR candidates…'}
        </div>
      )}

      <div className="flex items-center gap-2">
        {cta.mode === 'INBOX' ? (
          <button
            type="button"
            onClick={goPacketInbox}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-sky-600 text-white text-sm font-medium hover:bg-sky-700"
          >
            {cta.label} <ArrowRight className="w-4 h-4" />
          </button>
        ) : (
          <button
            type="button"
            onClick={handleContinue}
            disabled={!cta.canProceed || busy}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-sky-600 text-white text-sm font-medium hover:bg-sky-700 disabled:bg-slate-300 disabled:cursor-not-allowed"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
            {cta.label}
          </button>
        )}
        <button
          type="button"
          onClick={goHome}
          disabled={busy}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-slate-300 text-slate-700 text-sm font-medium hover:bg-slate-50 disabled:opacity-50"
        >
          Cancel
        </button>
      </div>

      <p className="text-xs text-slate-400 mt-4">
        ReferralGate never stores EHR tokens or patient identifiers in your browser. The referral
        is created only after you review and submit it in the Gate.
      </p>
    </Shell>
  );
};

export default SmartLaunchContinueView;
